<template>
  <div class="requests-view">
    <div class="page-header">
      <h1>Equipment Requests</h1>
      <p>Manage student equipment requests</p>
    </div>

    <!-- Stats Cards -->
    <!-- <RequestsStatsCards :stats="stats" /> -->

    <!-- Filters and Search -->
    <RequestsFilters
      v-model:searchQuery="searchQuery"
      v-model:filterStatus="filterStatus"
      v-model:filterSchool="filterSchool"
      :schoolOptions="schoolFilterOptions"
    />

    <!-- Requests Table -->
    <RequestsTable
      :requests="filteredRequests"
      :loading="loading"
      :schools="schools"
      :isAdmin="isAdmin"
      :selected-items="selectedRequests"
      @update:selectedItems="selectedRequests = $event"
      @selectRequest="selectRequest"
      @fill="openFillModal"
      @markDelivered="markAsDelivered"
      @edit="openEditModal"
      @delete="openDeleteModal"
      @bulkEdit="openBulkEditModal"
      @bulkMarkDelivered="bulkMarkDelivered"
      @printDeliverySlips="printDeliverySlips"
    />

    <!-- Request Details Modal -->
    <RequestDetailsDialog
      :request="selectedRequest"
      @close="closeModal"
      @fill-request="openFillModal"
      @mark-delivered="markAsDelivered"
    />

    <!-- Equipment Selection Modal -->
    <EquipmentSelectionModal
      v-model:show="showFillModal"
      :request="requestToFill"
      :equipment="availableEquipment"
      :loading="loadingEquipment"
      :schools="schools"
      @claim="openClaimModal"
      @unclaim="unclaimEquipment"
    />

    <!-- Edit Request Modal -->
    <v-dialog v-model="showEditModal" max-width="800px">
      <v-card>
        <v-card-title>
          <h2>Edit Request</h2>
        </v-card-title>
        <v-card-text>
          <v-form ref="editFormRef">
            <v-row>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="editFormData.student_name"
                  label="Student Name"
                  variant="outlined"
                  :rules="[v => !!v || 'Student name is required']"
                  required
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-select
                  v-model="editFormData.school"
                  :items="schoolOptions"
                  label="School"
                  variant="outlined"
                  :rules="[v => !!v || 'School is required']"
                  required
                ></v-select>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" sm="6">
                <v-select
                  v-model="editFormData.equipment_type"
                  :items="equipmentTypeOptions"
                  label="Equipment Type"
                  variant="outlined"
                  :rules="[v => !!v || 'Equipment type is required']"
                  required
                ></v-select>
              </v-col>
              <v-col cols="12" sm="6">
                <v-select
                  v-model="editFormData.status"
                  :items="statusOptions"
                  label="Status"
                  variant="outlined"
                  :rules="[v => !!v || 'Status is required']"
                  required
                ></v-select>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12">
                <v-select
                  v-model="editFormData.needed_for"
                  :items="taskOptions"
                  label="Needed For (Tasks)"
                  variant="outlined"
                  multiple
                  chips
                  closable-chips
                ></v-select>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model.number="editFormData.minimum_rating"
                  label="Minimum Rating"
                  type="number"
                  min="1"
                  max="10"
                  variant="outlined"
                  :rules="[v => v >= 1 && v <= 10 || 'Rating must be between 1 and 10']"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <div class="d-flex align-center ga-2">
                  <v-select
                    v-model="editFormData.assignee"
                    :items="userOptions"
                    label="Assignee (Optional)"
                    variant="outlined"
                    hint="Person responsible for fulfilling this request"
                    persistent-hint
                    clearable
                    class="flex-grow-1"
                  ></v-select>
                  <v-btn
                    icon="mdi-email-send"
                    color="primary"
                    variant="tonal"
                    size="small"
                    :disabled="!editFormData.teacher_email && !editFormData.assignee"
                    :loading="sendingNotification"
                    @click="notifyTeacher"
                    :title="editFormData.assignee ? 'Send notification to selected assignee' : 'Send notification to teacher email'"
                  ></v-btn>
                </div>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model="editFormData.teacher_email"
                  label="Teacher's Email"
                  type="email"
                  variant="outlined"
                  placeholder="teacher@school.edu"
                ></v-text-field>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12">
                <v-textarea
                  v-model="editFormData.notes"
                  label="Notes"
                  variant="outlined"
                  rows="3"
                  placeholder="Additional information or special requirements..."
                ></v-textarea>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" @click="closeEditModal">Cancel</v-btn>
          <v-btn color="primary" @click="saveRequest" :loading="submittingEdit">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Claim Equipment Modal -->
    <v-dialog v-model="showClaimModal" max-width="500px">
      <v-card>
        <v-card-title>
          <h2>Claim Equipment</h2>
        </v-card-title>
        <v-card-text>
          <p class="text-body-1 mb-4">
            Claim <strong>{{ equipmentToClaim?.manufacturer }} {{ equipmentToClaim?.model }}</strong> for <strong>{{ requestToFill?.student_name }}</strong>?
          </p>
          <v-alert type="info" variant="tonal">
            This will mark the equipment as claimed for this request.
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" @click="closeClaimModal">Cancel</v-btn>
          <v-btn color="success" @click="confirmClaim">Claim for Request</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Mark Delivered Confirmation Modal -->
    <v-dialog v-model="showDeliveredModal" :max-width="requestsToDeliver.length > 1 ? '800px' : '500px'">
      <v-card>
        <v-card-title class="text-h5">
          Confirm Delivery
        </v-card-title>
        <v-card-text>
          <!-- Single request view -->
          <div v-if="requestsToDeliver.length === 1">
            <p class="text-body-1 mb-4">
              Mark request for <strong>{{ requestsToDeliver[0]?.student_name }}</strong> as delivered?
            </p>
            <v-alert v-if="requestsToDeliver[0]?.assigned_asset_tag" type="info" variant="tonal" class="mb-4">
              <div class="text-subtitle-2 mb-1">Equipment Asset Tag:</div>
              <div class="text-h6 font-weight-bold">{{ requestsToDeliver[0].assigned_asset_tag }}</div>
            </v-alert>
          </div>

          <!-- Multiple requests view -->
          <div v-else>
            <p class="text-body-1 mb-4">
              Mark <strong>{{ requestsToDeliver.length }}</strong> requests as delivered?
            </p>
            <v-table density="compact" class="mb-4">
              <thead>
                <tr>
                  <th>Student Name</th>
                  <th>Asset Tag</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="req in requestsToDeliver" :key="req.id">
                  <td>{{ req.student_name }}</td>
                  <td>
                    <v-chip v-if="req.assigned_asset_tag" size="small" color="primary">
                      {{ req.assigned_asset_tag }}
                    </v-chip>
                    <span v-else class="text-grey">—</span>
                  </td>
                </tr>
              </tbody>
            </v-table>
          </div>

          <v-alert type="success" variant="tonal">
            This will update {{ requestsToDeliver.length === 1 ? 'the request' : 'all requests' }} status to "delivered".
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" @click="closeDeliveredModal">Cancel</v-btn>
          <v-btn color="success" @click="confirmDelivered">Mark as Delivered</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Modal -->
    <v-dialog v-model="showDeleteModal" max-width="500px">
      <v-card>
        <v-card-title class="text-h5">
          Confirm Delete
        </v-card-title>
        <v-card-text>
          <p class="text-body-1 mb-4">
            Are you sure you want to delete the request for <strong>{{ requestToDelete?.student_name }}</strong>?
          </p>
          <v-alert type="warning" variant="tonal">
            This action cannot be undone.
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" @click="closeDeleteModal">Cancel</v-btn>
          <v-btn color="error" @click="confirmDelete" :loading="deletingRequest">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Bulk Edit Modal -->
    <BulkEditRequestModal
      :show="showBulkEditModal"
      :schools="schools"
      :item-count="bulkEditRequestIds.length"
      :submitting="bulkEditSubmitting"
      :error="bulkEditError"
      @close="closeBulkEditModal"
      @submit="confirmBulkEdit"
    />

    <!-- Snackbar for notifications -->
    <v-snackbar
      v-model="showSnackbar"
      :color="snackbarColor"
      :timeout="3000"
      location="top"
    >
      {{ snackbarMessage }}
      <template v-slot:actions>
        <v-btn
          variant="text"
          @click="showSnackbar = false"
        >
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { api } from '@/composables/api'
import { useAuthStore } from '@/store/auth'
import RequestsStatsCards from '@/components/requests/RequestsStatsCards.vue'
import RequestsFilters from '@/components/requests/RequestsFilters.vue'
import RequestsTable from '@/components/requests/RequestsTable.vue'
import RequestDetailsDialog from '@/components/requests/RequestDetailsDialog.vue'
import EquipmentSelectionModal from '@/components/requests/EquipmentSelectionModal.vue'
import BulkEditRequestModal from '@/components/requests/BulkEditRequestModal.vue'

const authStore = useAuthStore()
const loading = ref(true)
const requests = ref<any[]>([])
const stats = ref<any>({})
const searchQuery = ref('')
const filterStatus = ref<string[]>(['pending', 'filled'])
const filterSchool = ref('')
const selectedRequest = ref<any>(null)
const showFillModal = ref(false)
const requestToFill = ref<any>(null)
const loadingEquipment = ref(false)
const availableEquipment = ref<any[]>([])
const showEditModal = ref(false)
const requestToEdit = ref<any>(null)
const editFormRef = ref<any>(null)
const submittingEdit = ref(false)
const showClaimModal = ref(false)
const equipmentToClaim = ref<any>(null)
const schools = ref<any[]>([])
const showDeliveredModal = ref(false)
const requestsToDeliver = ref<any[]>([])
const showDeleteModal = ref(false)
const requestToDelete = ref<any>(null)
const deletingRequest = ref(false)
const selectedRequests = ref<string[]>([])
const showBulkEditModal = ref(false)
const bulkEditRequestIds = ref<string[]>([])
const bulkEditSubmitting = ref(false)
const bulkEditError = ref('')
const users = ref<any[]>([])
const sendingNotification = ref(false)

// Snackbar state
const showSnackbar = ref(false)
const snackbarMessage = ref('')
const snackbarColor = ref('success')

// Edit form data
const editFormData = ref({
  student_name: '',
  school: '',
  equipment_type: '',
  status: '',
  needed_for: [] as string[],
  minimum_rating: 1,
  assignee: '',
  teacher_email: '',
  notes: ''
})

// Dropdown options
const schoolOptions = computed(() => schools.value.map(s => ({
  title: s.name,
  value: s.id || s._id
})))
const equipmentTypeOptions = [
  { title: 'Laptop', value: 'laptop' },
  { title: 'Desktop', value: 'desktop' },
  { title: 'Monitor', value: 'monitor' },
  { title: 'Tablet', value: 'tablet' },
  { title: 'Other', value: 'other' }
]
const statusOptions = [
  { title: 'Pending', value: 'pending' },
  { title: 'Filled', value: 'filled' },
  { title: 'Completed', value: 'completed' },
  { title: 'Denied', value: 'denied' }
]
const taskOptions = [
  { title: 'Email', value: 'email' },
  { title: 'Web Browsing', value: 'web_browsing' },
  { title: 'Office Work', value: 'office_work' },
  { title: 'Document Editing', value: 'document_editing' },
  { title: 'Video Conferencing', value: 'video_conferencing' },
  { title: 'Presentations', value: 'presentations' },
  { title: 'Programming/Coding', value: 'coding' },
  { title: 'Photo Editing', value: 'photo_editing' },
  { title: 'Video Editing', value: 'video_editing' },
  { title: '3D Modeling', value: '3d_modeling' },
  { title: 'Gaming', value: 'gaming' },
  { title: 'Creative Work', value: 'creative_work' },
  { title: 'Machine Learning', value: 'machine_learning' },
  { title: 'CAD', value: 'cad' },
  { title: 'Music Production', value: 'music_production' },
  { title: 'Animation', value: 'animation' }
]

const isAdmin = computed(() => authStore.authUser && authStore.authUser.isAdmin())
const userSchool = computed(() => authStore.authUser?.school || null)

// User options for assignee dropdown - only users with a school
const userOptions = computed(() => {
  return users.value
    .filter(user => user.school) // Only users with a school
    .map(user => ({
      title: `${user.first_name || ''} ${user.last_name || ''} (${user.email})`.trim(),
      value: user.id
    }))
})

// School filter options
const schoolFilterOptions = computed(() => {
  const options = [{ title: 'All Schools', value: '' }]
  schools.value.forEach(school => {
    options.push({ title: school.name, value: school.id || school._id })
  })
  return options
})

// Notification helper
const showNotification = (message: string, color: string = 'success') => {
  snackbarMessage.value = message
  snackbarColor.value = color
  showSnackbar.value = true
}

const filteredRequests = computed(() => {
  let filtered = requests.value

  // Filter by school
  if (filterSchool.value) {
    filtered = filtered.filter(r => r.school === filterSchool.value)
  }

  // Filter by status
  if (filterStatus.value && filterStatus.value.length > 0) {
    filtered = filtered.filter(r => filterStatus.value.includes(r.status))
  }

  // Search by student name or school
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    filtered = filtered.filter(r =>
      (r.student_name?.toLowerCase() || '').includes(query) ||
      (r.school?.toLowerCase() || '').includes(query)
    )
  }

  // Sort by submitted date (oldest first - FIFO)
  filtered.sort((a, b) => {
    const dateA = new Date(a.submitted_at || 0).getTime()
    const dateB = new Date(b.submitted_at || 0).getTime()
    return dateA - dateB
  })

  return filtered
})

const loadRequests = async () => {
  loading.value = true
  try {
    const response = await api.get('/requests')
    if (response.data) {
      requests.value = response.data
    }
  } catch (error) {
    console.error('Error loading requests:', error)
  } finally {
    loading.value = false
  }
}

const loadStats = async () => {
  try {
    const response = await api.get('/requests/stats')
    if (response.data) {
      stats.value = response.data
    }
  } catch (error) {
    console.error('Error loading stats:', error)
  }
}

const loadSchools = async () => {
  try {
    const response = await api.get('/schools')
    schools.value = response.data || response || []
  } catch (error) {
    console.error('Error loading schools:', error)
    schools.value = []
  }
}

const loadUsers = async () => {
  try {
    const response = await api.get('/users')
    users.value = response.data || response || []
  } catch (error) {
    console.error('Error loading users:', error)
    users.value = []
  }
}

const fulfillRequest = async (request: any) => {
  // This function is no longer used - kept for compatibility
  try {
    await api.patch(`/requests/${request.id}/fulfill`)
    await loadRequests()
    await loadStats()
    if (selectedRequest.value && selectedRequest.value.id === request.id) {
      selectedRequest.value = null
    }
    showNotification('Request marked as fulfilled', 'success')
  } catch (error) {
    console.error('Error fulfilling request:', error)
    showNotification('Failed to fulfill request', 'error')
  }
}

const markAsDelivered = (request: any) => {
  requestsToDeliver.value = [request]
  showDeliveredModal.value = true
}

const selectRequest = (request: any) => {
  selectedRequest.value = request
}

const openEditModal = (request: any) => {
  requestToEdit.value = request

  // Populate edit form
  editFormData.value = {
    student_name: request.student_name || '',
    school: request.school || '',
    equipment_type: request.equipment_type || '',
    status: request.status || '',
    needed_for: request.needed_for || [],
    minimum_rating: request.minimum_rating || 1,
    assignee: request.assignee || '',
    teacher_email: request.teacher_email || '',
    notes: request.notes || ''
  }

  showEditModal.value = true
}

const closeEditModal = () => {
  requestToEdit.value = null
  showEditModal.value = false
  editFormData.value = {
    student_name: '',
    school: '',
    equipment_type: '',
    status: '',
    needed_for: [],
    minimum_rating: 1,
    assignee: '',
    teacher_email: '',
    notes: ''
  }
}

const saveRequest = async () => {
  if (!requestToEdit.value) return

  // Validate form
  const { valid } = await editFormRef.value.validate()
  if (!valid) return

  submittingEdit.value = true

  try {
    await api.patch(`/requests/${requestToEdit.value.id}`, editFormData.value)

    await loadRequests()
    await loadStats()
    closeEditModal()
    showNotification('Request updated successfully', 'success')
  } catch (error: any) {
    console.error('Error updating request:', error)
    showNotification(error.response?.data?.detail || 'Failed to update request', 'error')
  } finally {
    submittingEdit.value = false
  }
}

const closeModal = () => {
  selectedRequest.value = null
}

const loadEquipmentForRequest = async () => {
  if (!requestToFill.value) return

  loadingEquipment.value = true

  try {
    // Get only available equipment
    const params: any = {
      equipment_type: requestToFill.value.equipment_type,
      status: 'available'
    }

    const response = await api.get('/equipment', params)

    if (response.data) {
      // Store all available equipment - the modal will handle sorting and matching
      availableEquipment.value = response.data
    }
  } catch (error) {
    console.error('Error loading equipment:', error)
    availableEquipment.value = []
  } finally {
    loadingEquipment.value = false
  }
}

const openFillModal = async (request: any) => {
  requestToFill.value = request
  showFillModal.value = true
  await loadEquipmentForRequest()
}

const closeFillModal = () => {
  showFillModal.value = false
  requestToFill.value = null
  availableEquipment.value = []
}

const openClaimModal = (equipment: any) => {
  equipmentToClaim.value = equipment
  showClaimModal.value = true
}

const closeClaimModal = () => {
  showClaimModal.value = false
  equipmentToClaim.value = null
}

const confirmClaim = async () => {
  if (!equipmentToClaim.value || !requestToFill.value) return

  try {
    // Claim equipment for the request
    await api.patch(`/equipment/${equipmentToClaim.value.id}/claim?claimed_for=${encodeURIComponent(requestToFill.value.id)}`)

    // Update request status to 'filled'
    await api.patch(`/requests/${requestToFill.value.id}/status`, {
      status: 'filled',
      assigned_equipment_id: equipmentToClaim.value.id
    })

    // Reload equipment list and close modal
    await loadEquipmentForRequest()
    closeClaimModal()
    closeFillModal()

    // Reload requests to show updated status
    await loadRequests()
    await loadStats()
    // Refresh badge count
    authStore.fetchSchoolRequestsCount()
    showNotification('Equipment claimed successfully', 'success')
  } catch (error: any) {
    console.error('Error claiming equipment:', error)

    // Check if equipment was already claimed
    if (error.response?.status === 400 && error.response?.data?.detail?.includes('not available')) {
      showNotification('This equipment has already been claimed by another user. Please refresh the page to see updated equipment.', 'warning')

      // Reload equipment list to show current state
      await loadEquipmentForRequest()
      closeClaimModal()
    } else {
      showNotification(error.response?.data?.detail || 'Failed to claim equipment', 'error')
    }
  }
}

const unclaimEquipment = async (equipment: any) => {
  try {
    await api.patch(`/equipment/${equipment.id}/unclaim`)

    // Reload equipment list
    await loadEquipmentForRequest()
    showNotification('Equipment unclaimed', 'success')
  } catch (error) {
    console.error('Error unclaiming equipment:', error)
    showNotification('Failed to unclaim equipment', 'error')
  }
}

const closeDeliveredModal = () => {
  showDeliveredModal.value = false
  requestsToDeliver.value = []
}

const confirmDelivered = async () => {
  if (!requestsToDeliver.value || requestsToDeliver.value.length === 0) return

  try {
    // Update each request status to 'completed'
    for (const request of requestsToDeliver.value) {
      await api.patch(`/requests/${request.id}/status`, {
        status: 'completed'
      })
    }

    await loadRequests()
    await loadStats()
    // Refresh badge count
    authStore.fetchSchoolRequestsCount()

    // Clear selected request if it was delivered
    if (selectedRequest.value && requestsToDeliver.value.some(r => r.id === selectedRequest.value.id)) {
      selectedRequest.value = null
    }

    // Clear selected items
    selectedRequests.value = []

    closeDeliveredModal()
    const count = requestsToDeliver.value.length
    showNotification(`${count} request${count > 1 ? 's' : ''} marked as delivered`, 'success')
  } catch (error) {
    console.error('Error marking request as delivered:', error)
    showNotification('Failed to mark request as delivered', 'error')
  }
}

const openDeleteModal = (request: any) => {
  requestToDelete.value = request
  showDeleteModal.value = true
}

const closeDeleteModal = () => {
  showDeleteModal.value = false
  requestToDelete.value = null
}

const confirmDelete = async () => {
  if (!requestToDelete.value) return

  deletingRequest.value = true

  try {
    await api.delete(`/requests/${requestToDelete.value.id}`)
    await loadRequests()
    await loadStats()
    // Refresh badge count
    authStore.fetchSchoolRequestsCount()
    if (selectedRequest.value && selectedRequest.value.id === requestToDelete.value.id) {
      selectedRequest.value = null
    }
    closeDeleteModal()
    showNotification('Request deleted successfully', 'success')
  } catch (error: any) {
    console.error('Error deleting request:', error)
    showNotification(error.response?.data?.detail || 'Failed to delete request', 'error')
  } finally {
    deletingRequest.value = false
  }
}

const bulkMarkDelivered = (requestIds: string[]) => {
  if (!requestIds || requestIds.length === 0) return

  // Get the full request objects from the ids
  const requestsToMark = requests.value.filter(r => requestIds.includes(r.id))
  requestsToDeliver.value = requestsToMark
  showDeliveredModal.value = true
}

const printDeliverySlips = async (requestIds: string[]) => {
  try {
    // Call the delivery slips API endpoint - it returns HTML
    const response = await api.post('/requests/delivery-slips', requestIds)

    // Open the HTML in a new window for printing
    const printWindow = window.open('', '_blank')
    if (printWindow) {
      printWindow.document.write(response.data)
      printWindow.document.close()
      // Optionally trigger print dialog
      setTimeout(() => {
        printWindow.focus()
        // printWindow.print() // Uncomment to auto-open print dialog
      }, 250)
    } else {
      showNotification('Please allow popups for this site to print delivery slips.', 'warning')
    }
  } catch (error: any) {
    console.error('Error generating delivery slips:', error)
    showNotification('Failed to generate delivery slips. Please try again.', 'error')
  }
}

const openBulkEditModal = (requestIds: string[]) => {
  bulkEditRequestIds.value = requestIds
  bulkEditError.value = ''
  showBulkEditModal.value = true
}

const closeBulkEditModal = () => {
  showBulkEditModal.value = false
  bulkEditRequestIds.value = []
  bulkEditError.value = ''
}

const confirmBulkEdit = async (updateData: any) => {
  if (!bulkEditRequestIds.value.length) {
    bulkEditError.value = 'No requests selected'
    return
  }

  // Check if any fields were filled in
  if (Object.keys(updateData).length === 0) {
    bulkEditError.value = 'Please fill in at least one field to update'
    return
  }

  bulkEditSubmitting.value = true
  bulkEditError.value = ''

  try {
    // Call bulk update API for each request
    for (const requestId of bulkEditRequestIds.value) {
      await api.patch(`/requests/${requestId}`, updateData)
    }

    await loadRequests()
    await loadStats()
    selectedRequests.value = []
    closeBulkEditModal()
    showNotification(`${bulkEditRequestIds.value.length} request(s) updated successfully`, 'success')
  } catch (error: any) {
    console.error('Error bulk updating requests:', error)
    if (error.response?.status === 403) {
      bulkEditError.value = 'You do not have permission to perform bulk updates.'
    } else {
      bulkEditError.value = error.response?.data?.detail || 'Failed to update requests. Please try again.'
    }
  } finally {
    bulkEditSubmitting.value = false
  }
}

const notifyTeacher = async () => {
  if (!editFormData.value.teacher_email || !requestToEdit.value) {
    showNotification('No teacher email to notify', 'error')
    return
  }

  sendingNotification.value = true

  try {
    // Get the assignee email from the current form selection
    let recipientEmail = editFormData.value.teacher_email

    // If an assignee is selected, find their email
    if (editFormData.value.assignee) {
      const selectedUser = users.value.find(u => u.id === editFormData.value.assignee)
      if (selectedUser && selectedUser.email) {
        recipientEmail = selectedUser.email
      }
    }

    await api.post(`/notifications/notify-teacher-about-request/${requestToEdit.value.id}`, {
      override_email: recipientEmail
    })
    showNotification(`Notification sent to ${recipientEmail}`, 'success')
  } catch (error: any) {
    console.error('Error sending notification:', error)
    showNotification(error.response?.data?.detail || 'Failed to send notification', 'error')
  } finally {
    sendingNotification.value = false
  }
}

onMounted(() => {
  loadRequests()
  loadStats()
  loadSchools()
  loadUsers()
  // Refresh the school requests count when entering requests view
  authStore.fetchSchoolRequestsCount()
})
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&family=Inter:wght@400;500;600&display=swap');

.requests-view {
  max-width: 1600px;
  margin: 0 auto;
}

.clickable-table :deep(tbody tr) {
  cursor: pointer;
}

.page-header {
  margin-bottom: 2rem;
}

.page-header h1 {
  font-family: 'Poppins', sans-serif;
  font-size: 2rem;
  color: var(--primary-color, #2c4a6b);
  margin: 0 0 0.5rem 0;
}

.page-header p {
  color: var(--text-light, #666666);
  margin: 0;
}

@media (max-width: 768px) {
  .page-header h1 {
    font-size: 1.5rem;
  }
}
</style>
